import { useState } from 'react';

/**
 * True on the render where `value` differs (Object.is) from the previous
 * render's value, so a component can update its own state in the same render
 * that observes the change.
 *
 * - Use the result only to call state setters, never to choose rendered output:
 *   React discards the render that returned true and re-renders, where it is false.
 * - Pass primitives or stable identities. A fresh object or array each render
 *   is always "changed" and loops; derive a key (`toString()`, a template string).
 * - Pass `initial` when the mount itself must count as a change, e.g.
 *   `useHasChanged(error, null)` fires on mount if `error` is already set.
 *   An explicit `undefined` is indistinguishable from omitting it.
 * - Call unconditionally. For several inputs, call once each and combine the
 *   results; never `useHasChanged(a) || useHasChanged(b)` (short-circuit skips a hook).
 */
export function useHasChanged<T>(value: T, initial: T = value): boolean {
  const [prev, setPrev] = useState(initial);
  if (Object.is(prev, value)) return false;
  setPrev(value);
  return true;
}
